/**
 * Save Kiosk Configuration Use Case
 * Saves kiosk UI customization to operator settings
 *
 * CRITICAL: Uses MERGE strategy to preserve existing settings
 */

import { KioskConfigRepository } from '../data/kiosk-config-repository';
import { KioskUICustomization } from '../types/kiosk-types';
import { SaveConfigResult } from '../../core/types/device-config-types';

/**
 * Request for saving kiosk configuration
 */
export interface SaveKioskConfigRequest {
  /** Operator ID to save config for */
  operatorId: number;

  /** Configuration to save */
  config: KioskUICustomization;

  /** Whether to notify users of changes */
  notify?: boolean;
}

/**
 * Use case for saving kiosk UI customization
 *
 * Flow:
 * 1. Update metadata (modifiedAt timestamp)
 * 2. Repository fetches existing operator data
 * 3. Merge UI customization into existing settings
 * 4. Save merged settings back to API
 * 5. Existing settings (language, licensePlate, filter, etc.) are PRESERVED
 */
export class SaveKioskConfigUseCase {
  constructor(private readonly repository: KioskConfigRepository) {}

  async execute(request: SaveKioskConfigRequest): Promise<SaveConfigResult> {
    try {
      // Update metadata with current timestamp and operator ID
      const configToSave: KioskUICustomization = {
        ...request.config,
        metadata: {
          ...request.config.metadata,
          modifiedAt: new Date().toISOString(),
          operatorId: request.operatorId,
        },
      };

      // Repository handles:
      // 1. GET existing operator data
      // 2. Parse existing settings
      // 3. Merge uiCustomization under the key (preserves all existing fields!)
      // 4. POST merged settings
      const response = await this.repository.saveOperatorSettings(
        request.operatorId,
        configToSave,
        request.notify
      );

      if (!response.success) {
        return {
          success: false,
          error: response.message || 'Failed to save configuration',
        };
      }

      return {
        success: true,
        metadata: configToSave.metadata,
      };
    } catch (error) {
      console.error('Failed to save kiosk configuration:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to save configuration',
      };
    }
  }
}
